/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import datazoom from '@datazoom/collector_shaka';
import {
  createHttpClient,
  createLogger,
  createRequestBuilder,
  HttpResponse,
  Logger,
  LoggerLevel,
  UtilFactory,
} from '@quickplay/foundation';
import { toApplicationReportingData, toDeviceReportingData, toUserReportingData } from '../util/DataUtil';
import { Player } from '@quickplay/player-interface';
import { CustomEventBlacklistFilter } from '../../model/CustomEventBlacklistFilter';
import { AnalyticsReporterConfiguration } from '../configuration/AnalyticsReporterConfiguration';
import { DatazoomAnalyticsReporterConfiguration } from '../configuration/DatazoomAnalyticsReporterConfiguration';
import { Provider } from '../Provider';
import { DatazoomAnalyticsReporter } from './model/DatazoomAnalyticsReporter';
import { AdPlaybackEvent } from '../../event/AdPlaybackEvent';

const TAG_NAME = 'FL_ANALYTICS';

export class DatazoomAnalyticsReporterImpl implements DatazoomAnalyticsReporter {
  private deferred_: any;
  private static instance: DatazoomAnalyticsReporter;
  private logger?: Logger;
  private initialized: boolean;
  private blacklistedCustomEvents: string[];
  private defaultBlocklistedCustomEvents: string[] = [AdPlaybackEvent.AD_PLAYBACK_METRIC];

  private constructor() {
    this.initialized = false;
    this.blacklistedCustomEvents = [];
  }

  static getInstance(): DatazoomAnalyticsReporter {
    if (!DatazoomAnalyticsReporterImpl.instance) {
      DatazoomAnalyticsReporterImpl.instance = new DatazoomAnalyticsReporterImpl();
    }

    return DatazoomAnalyticsReporterImpl.instance;
  }

  get provider(): Provider {
    return Provider.DATAZOOM;
  }

  initialize(configuration: AnalyticsReporterConfiguration): Promise<void> {
    this.deferred_ = new UtilFactory.Deferred<void>();

    this.logger = configuration.logger || createLogger(LoggerLevel.OFF, TAG_NAME);

    const datazoomConfig = configuration as DatazoomAnalyticsReporterConfiguration;

    datazoom.init({
      ['configuration_id']: datazoomConfig.configId,
    });

    // To set the datazoom logger level by using fl logger level.
    this.setDatazoomLoggerLevel(this.logger);

    this.initialized = true;

    // assemble metadata
    let metadata: { [key: string]: any } = {
      ...toApplicationReportingData(configuration.application),
    };

    if (configuration.device) {
      metadata = {
        ...metadata,
        ...toDeviceReportingData(configuration.device),
      };
    }

    if (configuration.user) {
      metadata = {
        ...metadata,
        ...toUserReportingData(configuration.user),
      };
    }

    this.reportMetadata(this.removeUndefinedData(metadata));

    if (configuration.customEventsConfigUrl) {
      this.getBlacklistedCustomEvents(configuration.customEventsConfigUrl)
        .then(response => {
          this.blacklistedCustomEvents = response;

          this.deferred_.resolve();
        })
        .catch(error => {
          this.deferred_.reject(error);
        });

      return this.deferred_.promise;
    } else {
      this.deferred_.resolve();

      return this.deferred_.promise;
    }
  }

  createContext(player: Player): any {
    return datazoom.createContext(player.rawPlayer);
  }

  reportEvent(eventName: string, metadata: { [key: string]: any } = {}, context: any = datazoom): void {
    if (!this.isInitialized()) {
      this.logger?.log('[ANALYTICS] analytics reporter not initialized');
      return;
    }

    if (!this.canReportEvent(eventName)) {
      this.logger?.log(`[ANALYTICS] skip reporting custom event "${eventName}"`);
      return;
    }

    const eventMetadata: { [p: string]: any } = this.removeUndefinedData(metadata);

    this.logger?.log('[ANALYTICS] reporting event', eventName, eventMetadata);

    context.generateEvent(eventName, eventMetadata);
  }

  reportMetadata(metadata: { [key: string]: any }, playerContext?: any): void {
    if (!this.isInitialized()) {
      this.logger?.log('[ANALYTICS] analytics reporter not initialized');
      return;
    }

    const context = playerContext ?? datazoom;

    const oldMetadata = context.getMetadata();

    let newMetadata = {
      ...oldMetadata,
      ...metadata,
    };

    newMetadata = this.removeUndefinedData(newMetadata);

    this.logger?.log('[ANALYTICS] reporting metadata', newMetadata);

    context.setMetadata(newMetadata);
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  reset(): void {
    datazoom.setMetadata(null);
  }

  private getBlacklistedCustomEvents = (customEventsConfigUrl: string): Promise<string[]> => {
    return new Promise((resolve, reject) => {
      const request = createRequestBuilder<any>().url(customEventsConfigUrl).get().build();

      const httpClient = createHttpClient<any, CustomEventBlacklistFilter>();

      httpClient
        .callWith(request)
        .then((response: HttpResponse<CustomEventBlacklistFilter>) => {
          if (response.body && response.body.data['black_listed_custom_events']) {
            resolve(response.body.data['black_listed_custom_events']);
          } else {
            resolve([]);
          }
        })
        .catch(error => {
          this.logger?.log(
            `[ANALYTICS] error retrieving blacklisted custom events from ${customEventsConfigUrl}`,
            error,
          );

          reject(error);
        });
    });
  };

  /**
   * Removes undefined or null properties from the provided map.
   * @param metadata the map to remove undefined or null properties
   * @private
   */
  private removeUndefinedData(metadata?: { [key: string]: any }): { [p: string]: any } {
    const newMetadata = {
      ...metadata,
    };

    Object.keys(newMetadata).forEach((key: string) =>
      newMetadata[key] === undefined || newMetadata[key] === null ? delete newMetadata[key] : {},
    );

    return newMetadata;
  }

  /**
   * Determines whether the given event name can be reported to datazoom or not.
   * @param eventName the event name
   * @private
   */
  private canReportEvent(eventName: string): boolean {
    return (
      !this.blacklistedCustomEvents.includes(eventName) && !this.defaultBlocklistedCustomEvents.includes(eventName)
    );
  }

  /**
   * Sets the logger level for the datazoom library.
   * @param logLevel - The desired log level from the LoggerLevel enum.
   */
  private setDatazoomLoggerLevel(logger: Logger): void {
    let dzLogLevel: string;

    switch (logger?.logLevel) {
      case LoggerLevel.ERROR:
        dzLogLevel = 'error';
        break;
      case LoggerLevel.WARN:
        dzLogLevel = 'warning';
        break;
      case LoggerLevel.INFO:
        dzLogLevel = 'info';
        break;
      case LoggerLevel.DEBUG:
        dzLogLevel = 'debug';
        break;
      case LoggerLevel.VERBOSE:
        dzLogLevel = 'verbose';
        break;
      default:
        dzLogLevel = 'off';
        break;
    }

    logger?.log('Datazoom logger level', dzLogLevel);
    datazoom.setLogLevel(dzLogLevel);
  }
}
